import Box from '@components/Box';
import { cssJoin } from '@shared/util';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type ReactNode,
  useCallback,
} from 'react';

export type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  size?: 'small' | 'medium';
  variant?: 'text' | 'outlined' | 'contained';
  arrow?: 'forward' | 'none' | 'up' | 'down' | 'back';
  color?: 'neutral' | 'accent' | 'destructive' | 'subtle';
};

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      size,
      endIcon,
      loading,
      onClick,
      children,
      disabled,
      className,
      fullWidth,
      startIcon,
      arrow = 'none',
      type = 'button',
      variant = 'text',
      color = 'neutral',
      ...restProps
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      },
      [onClick, isDisabled],
    );

    return (
      <button
        ref={ref}
        {...restProps}
        type={type}
        disabled={isDisabled}
        onClick={handleClick}
        data-loading={loading || undefined}
        aria-disabled={isDisabled || undefined}
        data-color={color !== 'neutral' && color}
        data-arrow={arrow !== 'forward' && arrow}
        data-size={variant !== 'text' ? size : undefined}
        className={cssJoin(
          className,
          fullWidth && 'w-full',
          variant === 'text' && 'button-text',
          variant === 'contained' && 'button-filled',
          variant === 'outlined' && 'button-outlined',
        )}
      >
        {loading ? (
          <>
            <span className="invisible">
              {startIcon || endIcon ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={size === 'small' ? 4 : 8}
                >
                  {startIcon}
                  {children}
                  {endIcon}
                </Box>
              ) : (
                children
              )}
            </span>
            <progress className="spinner" aria-label="Loading" />
          </>
        ) : startIcon || endIcon ? (
          <Box
            display="flex"
            alignItems="center"
            gap={size === 'small' ? 4 : 8}
          >
            {startIcon}
            {children}
            {endIcon}
          </Box>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
